// src/utils/loopUtils.js - Utilities for measure range timing extraction and loop functionality

export class LoopUtils {
  /**
   * Extract timing boundaries from a selected measure range
   * @param {Object} selectedRange - { start: number, end: number }
   * @param {Array} allNotesWithTiming - Array of note events with timing info
   * @param {Object} osmd - OSMD instance for measure validation
   * @returns {Object} - { startTime: number, endTime: number, measureCount: number }
   */
  static extractTimingBoundaries(selectedRange, allNotesWithTiming, osmd) {
    
    
    if (!selectedRange || selectedRange.start === null || selectedRange.end === null) {
      return null;
    }

    if (!allNotesWithTiming || allNotesWithTiming.length === 0) {
      return null;
    }

    const { start: startMeasure, end: endMeasure } = selectedRange;
    
    // Validate measure numbers exist in the score
    if (!this.validateMeasureRange(startMeasure, endMeasure, osmd)) {
      return null;
    }

    // Build measure timing map first
    const measureTimingMap = this.buildMeasureTimingMap(osmd, allNotesWithTiming);

    // Find all notes within the measure range
    const notesInRange = this.findNotesInMeasureRange(
      startMeasure, 
      endMeasure, 
      allNotesWithTiming, 
      osmd,
      measureTimingMap
    );

    if (notesInRange.length === 0) {
      return null;
    }

    // Calculate boundaries - Use EXACTLY the notes we found with direct measure extraction
    // This ensures we loop precisely the notes that belong to the selected measures
    const noteTimes = notesInRange.map(note => note.startTime);
    const noteEndTimes = notesInRange.map(note => note.endTime);
    
    const startTime = Math.min(...noteTimes);
    const endTime = Math.max(...noteEndTimes);
    
    const measureCount = endMeasure - startMeasure + 1;

    const boundaries = {
      startTime,
      endTime,
      duration: endTime - startTime,
      measureCount,
      startMeasure,
      endMeasure,
      noteCount: notesInRange.length,
      notesInRange: notesInRange, // Pass the found notes to avoid double filtering
      measureTimingMap: measureTimingMap // Pass the timing map for event filtering
    };

    console.log(`\n📊 EXTRACTED TIMING BOUNDARIES:`);
    console.log(`   Measures ${startMeasure}-${endMeasure}`);
    console.log(`   Time range: ${startTime.toFixed(2)}s - ${endTime.toFixed(2)}s (duration: ${(endTime - startTime).toFixed(2)}s)`);
    console.log(`   Notes in range: ${notesInRange.length}`);

    return boundaries;
  }

  /**
   * Validate that the measure range exists in the score
   */
  static validateMeasureRange(startMeasure, endMeasure, osmd) {
    if (!osmd?.graphic?.measureList) {
      return false;
    }

    // Find the highest measure number in the score
    let maxMeasureNumber = 0;
    let minMeasureNumber = Infinity;

    for (let measureIndex = 0; measureIndex < osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && measure.MeasureNumber) {
          maxMeasureNumber = Math.max(maxMeasureNumber, measure.MeasureNumber);
          minMeasureNumber = Math.min(minMeasureNumber, measure.MeasureNumber);
        }
      }
    }

    const isValid = startMeasure >= minMeasureNumber && 
                   endMeasure <= maxMeasureNumber && 
                   startMeasure <= endMeasure;

    
    return isValid;
  }

  /**
   * Find all notes that belong to measures within the specified range
   */
  static findNotesInMeasureRange(startMeasure, endMeasure, allNotesWithTiming, osmd, measureTimingMap) {
    console.log(`\n🎯 FINDING NOTES IN MEASURE RANGE ${startMeasure}-${endMeasure}`);

    const notesInRange = [];

    // Check if notes have been expanded (have playbackIndex from repeat expansion)
    const hasExpandedNotes = allNotesWithTiming.some(note => note.playbackIndex !== undefined);
    console.log(`   Has expanded notes: ${hasExpandedNotes}`);

    // If notes are expanded, we need to find the maximum playbackIndex for the target measures
    // to ensure we only get notes from the LAST occurrence
    let targetPlaybackIndices = null;

    if (hasExpandedNotes) {
      // NEW ALGORITHM: Find a contiguous sequence of playbackIndex values that contains
      // all measures in the range. This handles cases where the selection spans repeated
      // and non-repeated sections (e.g., selecting measures 5-8 where 5-6 repeat but 7-8 are in first ending)

      // Group notes by playbackIndex to understand the playback sequence
      const notesByPlaybackIndex = new Map();
      allNotesWithTiming.forEach(note => {
        if (note.sourceNote && note.playbackIndex !== undefined) {
          const measureNumber = this.extractMeasureNumberFromNote(note.sourceNote);
          if (measureNumber !== null) {
            if (!notesByPlaybackIndex.has(note.playbackIndex)) {
              notesByPlaybackIndex.set(note.playbackIndex, new Set());
            }
            notesByPlaybackIndex.get(note.playbackIndex).add(measureNumber);
          }
        }
      });

      // Find contiguous sequences that contain ALL measures in our range
      const requiredMeasures = new Set();
      for (let m = startMeasure; m <= endMeasure; m++) {
        requiredMeasures.add(m);
      }

      console.log(`   Looking for contiguous sequence containing measures:`, Array.from(requiredMeasures));

      // Try to find a contiguous range of playbackIndices that contains all required measures
      const sortedIndices = Array.from(notesByPlaybackIndex.keys()).sort((a, b) => a - b);
      let bestRange = null;
      let bestStartIdx = Infinity;

      for (let startIdx = 0; startIdx < sortedIndices.length; startIdx++) {
        const foundMeasures = new Set();
        let endIdx = startIdx;

        // Expand the range until we have all required measures or can't expand anymore
        while (endIdx < sortedIndices.length) {
          const playbackIdx = sortedIndices[endIdx];
          const measuresAtIdx = notesByPlaybackIndex.get(playbackIdx);
          measuresAtIdx.forEach(m => foundMeasures.add(m));

          // Check if we have all required measures
          const hasAll = Array.from(requiredMeasures).every(m => foundMeasures.has(m));
          if (hasAll) {
            // Found a valid range! Use the first one we find (earliest in playback order)
            bestRange = { start: sortedIndices[startIdx], end: sortedIndices[endIdx] };
            bestStartIdx = startIdx;
            console.log(`   Found contiguous range: playbackIndex ${bestRange.start}-${bestRange.end}`);
            break;
          }

          endIdx++;
        }

        if (bestRange) break; // Use first valid range
      }

      if (bestRange) {
        // Use only notes within the contiguous playbackIndex range
        targetPlaybackIndices = { useRange: true, start: bestRange.start, end: bestRange.end };
        console.log(`   Using playbackIndex range ${bestRange.start}-${bestRange.end} for selection`);
      } else {
        // Fallback: use max playbackIndex for each measure (old behavior)
        console.log(`   WARNING: Could not find contiguous range, falling back to max playbackIndex`);
        const maxPlaybackIndexByMeasure = new Map();
        allNotesWithTiming.forEach(note => {
          if (note.sourceNote) {
            const measureNumber = this.extractMeasureNumberFromNote(note.sourceNote);
            if (measureNumber !== null && measureNumber >= startMeasure && measureNumber <= endMeasure) {
              const currentMax = maxPlaybackIndexByMeasure.get(measureNumber) ?? -1;
              maxPlaybackIndexByMeasure.set(measureNumber, Math.max(currentMax, note.playbackIndex ?? 0));
            }
          }
        });
        targetPlaybackIndices = { useRange: false, indices: maxPlaybackIndexByMeasure };
        console.log(`   Max playback indices by measure:`, Array.from(maxPlaybackIndexByMeasure.entries()));
      }
    }

    // Try to get measure number directly from source notes first (more accurate)
    allNotesWithTiming.forEach((note, index) => {
      let measureNumber = null;

      // First try: Get measure number directly from the source note
      if (note.sourceNote) {
        measureNumber = this.extractMeasureNumberFromNote(note.sourceNote);
      }

      // Fallback: Use timing-based approach if direct extraction fails
      if (measureNumber === null && measureTimingMap) {
        measureNumber = this.findMeasureForNoteTime(note.startTime, measureTimingMap);
      }

      if (measureNumber !== null && measureNumber >= startMeasure && measureNumber <= endMeasure) {
        // CRITICAL FIX: When notes are expanded, filter based on playbackIndex strategy
        if (hasExpandedNotes && targetPlaybackIndices) {
          if (targetPlaybackIndices.useRange) {
            // Use contiguous range approach
            if (note.playbackIndex < targetPlaybackIndices.start || note.playbackIndex > targetPlaybackIndices.end) {
              // Skip notes outside the playbackIndex range
              return;
            }
          } else {
            // Fallback: use max playbackIndex for each measure
            const targetIndex = targetPlaybackIndices.indices.get(measureNumber);
            if (targetIndex !== undefined && note.playbackIndex !== targetIndex) {
              // Skip this note - it's from an earlier occurrence of this measure
              return;
            }
          }
        }

        notesInRange.push({
          ...note,
          measureNumber,
          originalIndex: index,
          extractionMethod: note.sourceNote && this.extractMeasureNumberFromNote(note.sourceNote) !== null ? 'direct' : 'timing'
        });
      }
    });


    // Show extraction method statistics
    const directCount = notesInRange.filter(n => n.extractionMethod === 'direct').length;
    const timingCount = notesInRange.filter(n => n.extractionMethod === 'timing').length;

    console.log(`   Found ${notesInRange.length} notes total (${directCount} direct, ${timingCount} timing)`);

    // Show first and last 3 notes for debugging
    if (notesInRange.length > 0) {
      const firstNotes = notesInRange.slice(0, 3);
      const lastNotes = notesInRange.slice(-3);
      console.log(`   First note: measure ${firstNotes[0].measureNumber}, time ${firstNotes[0].startTime.toFixed(2)}, playbackIndex ${firstNotes[0].playbackIndex ?? 'none'}`);
      console.log(`   Last note: measure ${lastNotes[lastNotes.length-1].measureNumber}, time ${lastNotes[lastNotes.length-1].startTime.toFixed(2)}, playbackIndex ${lastNotes[lastNotes.length-1].playbackIndex ?? 'none'}`);

      // Group notes by measure to show the time range for each measure
      const notesByMeasure = new Map();
      notesInRange.forEach(note => {
        if (!notesByMeasure.has(note.measureNumber)) {
          notesByMeasure.set(note.measureNumber, []);
        }
        notesByMeasure.get(note.measureNumber).push(note);
      });
      console.log(`   Notes grouped by measure:`);
      notesByMeasure.forEach((notes, measureNum) => {
        const times = notes.map(n => n.startTime);
        const minTime = Math.min(...times);
        const maxTime = Math.max(...times);
        const playbackIdx = notes[0].playbackIndex ?? 'none';
        console.log(`      Measure ${measureNum}: ${notes.length} notes, time ${minTime.toFixed(2)}-${maxTime.toFixed(2)}, playbackIndex ${playbackIdx}`);
      });
    }

    return notesInRange;
  }

  /**
   * Build a map of measure numbers to their timing boundaries
   */
  static buildMeasureTimingMap(osmd, allNotesWithTiming) {
    const measureTimingMap = new Map();

    if (!osmd?.cursor || !allNotesWithTiming.length) {
      return measureTimingMap;
    }

    // Check if notes have been expanded (have playbackIndex from repeat expansion)
    const hasExpandedNotes = allNotesWithTiming.some(note => note.playbackIndex !== undefined);

    // Group notes by their source measure if available
    const notesByMeasure = new Map();

    allNotesWithTiming.forEach(note => {
      if (note.sourceNote && note.sourceNote.ParentVoiceEntry) {
        const measureNumber = this.extractMeasureNumberFromNote(note.sourceNote);
        if (measureNumber) {
          if (!notesByMeasure.has(measureNumber)) {
            notesByMeasure.set(measureNumber, []);
          }
          notesByMeasure.get(measureNumber).push(note);
        }
      }
    });

    // Calculate timing boundaries for each measure
    notesByMeasure.forEach((notes, measureNumber) => {
      // CRITICAL FIX: When notes are expanded due to repeats, we need to use
      // only the LAST occurrence of each measure, not all occurrences combined.
      // This ensures that clicking a measure jumps to its final position in the score.
      let relevantNotes = notes;

      if (hasExpandedNotes) {
        // Find the maximum playbackIndex for this measure
        const maxPlaybackIndex = Math.max(...notes.map(n => n.playbackIndex ?? 0));

        // Keep only notes from the last occurrence (highest playbackIndex)
        relevantNotes = notes.filter(n => n.playbackIndex === maxPlaybackIndex);
      }

      const startTime = Math.min(...relevantNotes.map(n => n.startTime));
      const endTime = Math.max(...relevantNotes.map(n => n.endTime));

      measureTimingMap.set(measureNumber, {
        startTime,
        endTime,
        noteCount: relevantNotes.length
      });
    });

    // If we couldn't map many measures, try a fallback approach
    if (measureTimingMap.size < 3) {
      return this.estimateMeasureTimingsFallback(osmd, allNotesWithTiming);
    }

    return measureTimingMap;
  }

  /**
   * Extract measure number from a source note
   */
  static extractMeasureNumberFromNote(sourceNote) {
    try {
      // FIRST: Try the sourceMeasure property that we can see in the debug output
      if (sourceNote.sourceMeasure?.MeasureNumber) {
        return sourceNote.sourceMeasure.MeasureNumber;
      }
      
      // SECOND: Try parentStaffEntry path
      if (sourceNote.parentStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.parentStaffEntry.ParentMeasure.MeasureNumber;
      }
      
      // THIRD: Try voiceEntry path  
      if (sourceNote.voiceEntry?.parentSourceStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.voiceEntry.parentSourceStaffEntry.ParentMeasure.MeasureNumber;
      }
      
      // FOURTH: Try other common paths
      if (sourceNote.ParentVoiceEntry?.ParentStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.ParentVoiceEntry.ParentStaffEntry.ParentMeasure.MeasureNumber;
      }
      
      if (sourceNote.ParentStaffEntry?.ParentMeasure?.MeasureNumber) {
        return sourceNote.ParentStaffEntry.ParentMeasure.MeasureNumber;
      }

      // Log what properties are actually available for debugging
      if (sourceNote) {
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Fallback method to estimate measure timings when direct mapping fails
   */
  static estimateMeasureTimingsFallback(osmd, allNotesWithTiming) {
    
    const measureTimingMap = new Map();
    
    if (!osmd?.graphic?.measureList || !allNotesWithTiming.length) {
      return measureTimingMap;
    }

    // Get total duration and number of measures
    const totalDuration = Math.max(...allNotesWithTiming.map(n => n.endTime));
    const measureCount = this.countMeasuresInScore(osmd);
    
    if (measureCount > 0) {
      const estimatedMeasureDuration = totalDuration / measureCount;
      
      
      // Create estimated boundaries for each measure (non-overlapping)
      for (let i = 1; i <= measureCount; i++) {
        const startTime = (i - 1) * estimatedMeasureDuration;
        const endTime = i * estimatedMeasureDuration;
        
        measureTimingMap.set(i, {
          startTime,
          endTime: endTime - 0.001, // Ensure non-overlapping by subtracting small amount
          noteCount: 0, // We don't know exact count in fallback
          estimated: true
        });
      }
    }

    return measureTimingMap;
  }

  /**
   * Count total measures in the score
   */
  static countMeasuresInScore(osmd) {
    if (!osmd?.graphic?.measureList) return 0;
    
    let maxMeasureNumber = 0;
    
    for (let measureIndex = 0; measureIndex < osmd.graphic.measureList.length; measureIndex++) {
      const measureRow = osmd.graphic.measureList[measureIndex];
      
      for (let staffIndex = 0; staffIndex < measureRow.length; staffIndex++) {
        const measure = measureRow[staffIndex];
        if (measure && measure.MeasureNumber) {
          maxMeasureNumber = Math.max(maxMeasureNumber, measure.MeasureNumber);
        }
      }
    }
    
    return maxMeasureNumber;
  }

  /**
   * Find which measure a given time falls into
   */
  static findMeasureForNoteTime(noteTime, measureTimingMap) {    
    for (const [measureNumber, timing] of measureTimingMap) {
      // Add small tolerance for boundary cases in fallback estimation
      const tolerance = timing.estimated ? 0.05 : 0.001; // 50ms tolerance for estimated boundaries
      
      if (noteTime >= (timing.startTime - tolerance) && noteTime < (timing.endTime + tolerance)) {
        return measureNumber;
      }
    }
    
    // If not found, find the closest measure
    let closestMeasure = 1;
    let closestDistance = Infinity;
    
    for (const [measureNumber, timing] of measureTimingMap) {
      const distance = Math.min(
        Math.abs(noteTime - timing.startTime),
        Math.abs(noteTime - timing.endTime)
      );
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestMeasure = measureNumber;
      }
    }
    
    return closestMeasure;
  }

  /**
   * Transform note events to loop-relative timing
   * @param {Array} allNotesWithTiming - Original note events
   * @param {Object} loopBoundaries - Loop boundaries from extractTimingBoundaries
   * @returns {Object} - { loopNotes: Array, originalNotes: Array }
   */
  static createLoopRelativeNotes(allNotesWithTiming, loopBoundaries) {

    if (!allNotesWithTiming || !loopBoundaries) {
      return { loopNotes: [], originalNotes: [] };
    }

    const { startTime: loopStart, endTime: loopEnd } = loopBoundaries;
    
    // Find all notes within the loop boundaries
    const notesInLoop = allNotesWithTiming.filter(note => {
      return note.startTime >= loopStart && note.startTime < loopEnd;
    });


    // Transform notes to start at time 0
    const loopNotes = notesInLoop.map((note, index) => {
      const transformedNote = {
        ...note,
        startTime: note.startTime - loopStart,
        endTime: note.endTime - loopStart,
        originalStartTime: note.startTime,
        originalEndTime: note.endTime,
        loopIndex: index
      };

      return transformedNote;
    });

    // Sort by new start time
    loopNotes.sort((a, b) => a.startTime - b.startTime);

    const result = {
      loopNotes,
      originalNotes: notesInLoop,
      loopDuration: loopBoundaries.duration,
      transformationOffset: loopStart
    };


    return result;
  }

  /**
   * Create loop-compatible note events for ToneJS scheduling
   * @param {Array} loopNotes - Loop-relative notes from createLoopRelativeNotes
   * @param {Array} originalNoteEvents - Original ToneJS events from noteEventsRef
   * @param {Object} loopBoundaries - Loop boundaries
   * @returns {Array} - Loop-compatible ToneJS events
   */
  static createLoopNoteEvents(loopNotes, originalNoteEvents, loopBoundaries) {

    if (!loopNotes || !originalNoteEvents || !loopBoundaries) {
      return [];
    }

    const { startTime: loopStart, endTime: loopEnd } = loopBoundaries;
    
    // Use EXACT note matching instead of timing boundaries for precision
    let originalLoopEvents;
    
    if (loopBoundaries.notesInRange && loopBoundaries.notesInRange.length > 0) {
      
      // Create a Set of exact timing signatures for the notes we want
      const targetNoteSignatures = new Set();
      loopBoundaries.notesInRange.forEach(note => {
        // Create a unique signature for each note: startTime + pitch + partId
        const signature = `${note.startTime.toFixed(6)}_${note.pitch}_${note.partId}`;
        targetNoteSignatures.add(signature);
      });
      
      
      // Find events that exactly match our target notes
      originalLoopEvents = originalNoteEvents.filter(event => {
        const eventSignature = `${event.time.toFixed(6)}_${event.pitch}_${event.partId}`;
        const isMatch = targetNoteSignatures.has(eventSignature);
        return isMatch;
      });
      
      
      // DEBUG: Find out why we lost 6 notes (26 notes -> 20 events)
      
      // Check what notes didn't get matched
      const matchedSignatures = new Set(originalLoopEvents.map(e => `${e.time.toFixed(6)}_${e.pitch}_${e.partId}`));
      const unmatchedNotes = loopBoundaries.notesInRange.filter(note => {
        const sig = `${note.startTime.toFixed(6)}_${note.pitch}_${note.partId}`;
        return !matchedSignatures.has(sig);
      });
      
      unmatchedNotes.forEach((note, i) => {
      });
    
    // Show first and last few events
    if (originalLoopEvents.length > 0) {
      originalLoopEvents.slice(0, 5).forEach((event, i) => {
      });
      
      originalLoopEvents.slice(-5).forEach((event, i) => {
      });
      
      // Sort events by time and show the chronological order
      const sortedEvents = [...originalLoopEvents].sort((a, b) => a.time - b.time);
      sortedEvents.forEach((event, i) => {
      });
    }
    } else {
      // Fallback: if we have measure timing info, use a more inclusive approach
      if (loopBoundaries.measureTimingMap && loopBoundaries.measureTimingMap.size > 0) {
        // Get the actual timing boundaries for the selected measures
        const startMeasureTiming = loopBoundaries.measureTimingMap.get(loopBoundaries.startMeasure);
        const endMeasureTiming = loopBoundaries.measureTimingMap.get(loopBoundaries.endMeasure);
        
        if (startMeasureTiming && endMeasureTiming) {
          const actualStart = startMeasureTiming.startTime;
          const actualEnd = endMeasureTiming.endTime;
          
          
          originalLoopEvents = originalNoteEvents.filter(event => {
            return event.time >= actualStart && event.time < actualEnd;
          });
        } else {
          // Final fallback
          originalLoopEvents = originalNoteEvents.filter(event => {
            return event.time >= loopStart && event.time < loopEnd;
          });
        }
      } else {
        originalLoopEvents = originalNoteEvents.filter(event => {
          return event.time >= loopStart && event.time < loopEnd;
        });
      }
      
    }


    // Transform to loop-relative timing
    const loopEvents = originalLoopEvents.map((event, index) => {
      const transformedEvent = {
        ...event,
        time: event.time - loopStart, // Shift to start at 0
        originalTime: event.time,
        loopIndex: index
      };

      return transformedEvent;
    });

    // Sort by new time
    loopEvents.sort((a, b) => a.time - b.time);

    if (loopEvents.length > 0) {
    }

    return loopEvents;
  }

  /**
   * Convert loop-relative time back to original time for highlighting
   * @param {number} loopTime - Time within the loop (0 to loopDuration)
   * @param {number} loopStartTime - Original start time of the loop
   * @returns {number} - Original timeline time
   */
  static loopTimeToOriginalTime(loopTime, loopStartTime) {
    return loopStartTime + loopTime;
  }

  /**
   * Convert original time to loop-relative time
   * @param {number} originalTime - Time on original timeline
   * @param {number} loopStartTime - Original start time of the loop
   * @param {number} loopDuration - Duration of the loop
   * @returns {number} - Time within loop (0 to loopDuration)
   */
  static originalTimeToLoopTime(originalTime, loopStartTime, loopDuration) {
    if (originalTime < loopStartTime) return 0;
    
    const relativeTime = originalTime - loopStartTime;
    return relativeTime % loopDuration; // Handle multiple loop cycles
  }

  /**
   * Test the timing transformation with logging
   * @param {Array} allNotesWithTiming - Original note events
   * @param {Array} originalNoteEvents - Original ToneJS events
   * @param {Object} loopBoundaries - Loop boundaries
   * @returns {Object} - Test results with transformed data
   */
  static testTimingTransformation(allNotesWithTiming, originalNoteEvents, loopBoundaries) {
    
    const transformedNotes = this.createLoopRelativeNotes(allNotesWithTiming, loopBoundaries);
    const transformedEvents = this.createLoopNoteEvents(
      transformedNotes.loopNotes, 
      originalNoteEvents, 
      loopBoundaries
    );

    const testResults = {
      originalNotesInLoop: transformedNotes.originalNotes.length,
      transformedNotes: transformedNotes.loopNotes.length,
      transformedEvents: transformedEvents.length,
      loopDuration: transformedNotes.loopDuration,
      firstNoteTime: transformedNotes.loopNotes[0]?.startTime || 0,
      lastNoteTime: transformedNotes.loopNotes[transformedNotes.loopNotes.length - 1]?.endTime || 0,
      firstEventTime: transformedEvents[0]?.time || 0,
      lastEventTime: transformedEvents[transformedEvents.length - 1]?.time || 0
    };

    
    return {
      ...testResults,
      transformedNotes,
      transformedEvents
    };
  }
}